import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { EmptyState, StatusBadge } from '@pazportal/ui';
import { supabase } from '../lib/supabase';
import { formatAvailabilityPct } from '../hooks/useCustomerNetworkLive';
import { MissionControlSiteLinks } from '../components/MissionControlSiteLinks';
import { MrcCollectorIncidentsCard } from '../components/MrcCollectorIncidentsCard';
import { MrcFlowPolicyCard } from '../components/MrcFlowPolicyCard';

interface SiteDevice {
  id: string;
  name: string | null;
  device_type: string | null;
  status: string | null;
  ip_address: string | null;
  vendor_name: string | null;
  availability_pct: number | null;
  last_seen_at: string | null;
}

interface SiteAlert {
  id: string;
  alert_name: string | null;
  severity: string | null;
  status: string | null;
  entity_name: string | null;
  message: string | null;
}

export function LocationNetworkPage() {
  const { id } = useParams<{ id: string }>();
  return <div className="space-y-5">
    {id && <NativeMonitoringLinks locationId={id} />}
    <NetworkRecords />
    {id && <MrcCollectorIncidentsCard key={id} locationId={id} />}
    {id && <MrcFlowPolicyCard key={id} locationId={id} />}
    {id && <MissionControlSiteLinks key={id} locationId={id} />}
  </div>;
}

function NativeMonitoringLinks({ locationId }: { locationId: string }) {
  const links = useQuery({
    queryKey: ['location-native-network-links', locationId],
    queryFn: async () => {
      const { data, error } = await supabase.from('mission_control_site_links')
        .select('id,mission_control_site_id,label').eq('location_id', locationId)
        .eq('status', 'verified').eq('staff_enabled', true).order('created_at');
      if (error) throw error;
      return data ?? [];
    },
    refetchInterval: 30_000,
  });
  if (!links.data?.length) return null;
  return <section aria-label="Network operations" className="rounded-lg border border-border bg-surface p-5"><h2 className="font-semibold text-text">Network operations</h2><p className="mt-1 text-sm text-text-muted">Explore devices, topology, performance, storage and system health inside Portal.</p><div className="mt-3 flex flex-wrap gap-2">{links.data.map((link) => <Link key={link.id} to={`/network-ops/site/${encodeURIComponent(link.mission_control_site_id)}`} className="inline-flex min-h-11 items-center rounded-md border border-border px-3 py-2 text-sm text-primary hover:bg-surface-2">{link.label || link.mission_control_site_id} →</Link>)}</div></section>;
}

function NetworkRecords() {
  const { id: locationId } = useParams<{ id: string }>();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin-location-network', locationId],
    enabled: Boolean(locationId),
    queryFn: async () => {
      const [devices, alerts] = await Promise.all([
        supabase
          .from('devices')
          .select('id, name, device_type, status, ip_address, vendor_name, availability_pct, last_seen_at')
          .eq('location_id', locationId!)
          .order('name'),
        supabase
          .from('network_alerts')
          .select('id, alert_name, severity, status, entity_name, message')
          .eq('location_id', locationId!)
          .or('status.is.null,and(status.neq.cleared,status.neq.resolved)')
          .order('detected_on', { ascending: false })
          .limit(25),
      ]);
      if (devices.error) throw devices.error;
      if (alerts.error) throw alerts.error;
      return {
        devices: (devices.data ?? []) as SiteDevice[],
        alerts: (alerts.data ?? []) as SiteAlert[],
      };
    },
  });

  if (isLoading) {
    return <p className="text-sm text-text-muted">Loading network…</p>;
  }

  if (isError) {
    return (
      <EmptyState
        icon={<NetworkIcon />}
        title="Unable to load network"
        subtitle="Device and alert records for this site are still in PazPortal. Refresh to try again."
      />
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold text-text sm:text-2xl">Network</h1>
        <p className="mt-1 text-sm text-text-muted">Live devices and open alerts for this site.</p>
      </div>

      <section className="rounded-lg border border-border bg-surface">
        <div className="border-b border-border px-5 py-4">
          <h2 className="font-semibold text-text">Open alerts ({data?.alerts.length ?? 0})</h2>
        </div>
        {!data?.alerts.length ? (
          <p className="px-5 py-6 text-sm text-text-muted">No open alerts for this site.</p>
        ) : (
          <ul className="divide-y divide-divider">
            {data.alerts.map((alert) => (
              <li key={alert.id} className="flex items-start justify-between gap-3 px-5 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-text">{alert.alert_name ?? alert.entity_name ?? 'Alert'}</p>
                  {alert.message && <p className="truncate text-xs text-text-muted">{alert.message}</p>}
                </div>
                {alert.severity && <StatusBadge status={alert.severity} />}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="overflow-x-auto rounded-lg border border-border bg-surface">
        <div className="border-b border-border px-5 py-4">
          <h2 className="font-semibold text-text">Devices ({data?.devices.length ?? 0})</h2>
        </div>
        {!data?.devices.length ? (
          <p className="px-5 py-6 text-sm text-text-muted">No devices mapped to this site.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-surface-3 text-left text-text-muted">
              <tr>
                <th className="px-4 py-3 font-medium">Device</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Availability</th>
                <th className="px-4 py-3 font-medium">IP</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-divider">
              {data.devices.map((device) => (
                <tr key={device.id}>
                  <td className="px-4 py-3">
                    <p className="font-medium text-text">{device.name ?? '—'}</p>
                    <p className="text-xs text-text-muted">{device.vendor_name ?? ''}</p>
                  </td>
                  <td className="px-4 py-3 text-text-muted">{device.device_type ?? '—'}</td>
                  <td className="px-4 py-3">{device.status ? <StatusBadge status={device.status} /> : '—'}</td>
                  <td className="px-4 py-3">{formatAvailabilityPct(device.availability_pct)}</td>
                  <td className="px-4 py-3 font-mono text-xs">{device.ip_address ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

function NetworkIcon() {
  return (
    <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17 8 20m8-3 1.75 3M3 13h18M5 5h14a2 2 0 0 1 2 2v6H3V7a2 2 0 0 1 2-2Z" />
    </svg>
  );
}
